import { useMemo } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { useI18nContext } from '@/i18n/i18n-react';
import { chartColor } from '@/lib/chart-colors';
import type { RepurchaseSegment } from '@/lib/orders-drilldown';

/** chart-1 — in-app repurchases */
export const IN_APP_REPURCHASE_COLOR = chartColor(1);
/** chart-2 — channel migration */
export const CHANNEL_MIGRATION_COLOR = chartColor(2);
/** chart-3 — first orders */
export const FIRST_PURCHASE_COLOR = chartColor(3);

const EMPTY_COLOR = 'hsl(var(--muted))';
const MIN_SLICE_VALUE = 1;

type SegmentKey =
  | 'firstPurchase'
  | 'inAppRepurchase'
  | 'channelMigration'
  | 'empty';

const SEGMENT_BY_KEY: Record<
  Exclude<SegmentKey, 'empty'>,
  RepurchaseSegment
> = {
  inAppRepurchase: 'in_app',
  channelMigration: 'channel_migration',
  firstPurchase: 'first_purchase',
};

type Segment = {
  key: SegmentKey;
  value: number;
  actualValue: number;
  label: string;
  color: string;
};

interface OrdersCompositionDonutProps {
  firstPurchaseCount: number;
  inAppRepurchaseCount: number;
  channelMigrationCount: number;
  /** When provided, slices are clickable. */
  onSegmentClick?: (segment: RepurchaseSegment) => void;
}

interface DonutTooltipPayloadItem {
  payload: Segment;
}

function DonutTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: DonutTooltipPayloadItem[];
}) {
  if (!active || !payload?.length) return null;
  const segment = payload[0]?.payload as Segment | undefined;
  if (!segment || segment.key === 'empty') return null;
  return (
    <div className="rounded-lg border border-input bg-background px-2 py-1.5 text-xs shadow-md">
      <p className="flex items-center gap-1.5 font-medium text-foreground">
        <span
          className="inline-block h-2 w-2 rounded-full"
          style={{ backgroundColor: segment.color }}
        />
        {segment.label}
      </p>
      <p className="text-muted-foreground">{segment.actualValue}</p>
    </div>
  );
}

export function OrdersCompositionDonut({
  firstPurchaseCount,
  inAppRepurchaseCount,
  channelMigrationCount,
  onSegmentClick,
}: OrdersCompositionDonutProps) {
  const { LL } = useI18nContext();

  const total =
    firstPurchaseCount + inAppRepurchaseCount + channelMigrationCount;

  const data = useMemo((): Segment[] => {
    if (total <= 0) {
      return [
        {
          key: 'empty',
          value: 1,
          actualValue: 0,
          label: '',
          color: EMPTY_COLOR,
        },
      ];
    }
    return [
      {
        key: 'inAppRepurchase',
        value:
          inAppRepurchaseCount > 0 ? inAppRepurchaseCount : MIN_SLICE_VALUE,
        actualValue: inAppRepurchaseCount,
        label: LL.metricsPage.ordersComposition.inAppRepurchase.label(),
        color: IN_APP_REPURCHASE_COLOR,
      },
      {
        key: 'channelMigration',
        value:
          channelMigrationCount > 0 ? channelMigrationCount : MIN_SLICE_VALUE,
        actualValue: channelMigrationCount,
        label: LL.metricsPage.ordersComposition.channelMigration.label(),
        color: CHANNEL_MIGRATION_COLOR,
      },
      {
        key: 'firstPurchase',
        value: firstPurchaseCount > 0 ? firstPurchaseCount : MIN_SLICE_VALUE,
        actualValue: firstPurchaseCount,
        label: LL.metricsPage.ordersComposition.firstPurchase.label(),
        color: FIRST_PURCHASE_COLOR,
      },
    ];
  }, [
    firstPurchaseCount,
    inAppRepurchaseCount,
    channelMigrationCount,
    total,
    LL.metricsPage.ordersComposition,
  ]);

  const hasAnyData = total > 0;

  const handleSliceClick = (_: unknown, index: number) => {
    const entry = data[index];
    if (!onSegmentClick || !entry || entry.key === 'empty') return;
    onSegmentClick(SEGMENT_BY_KEY[entry.key]);
  };
  const isClickable = onSegmentClick !== undefined && hasAnyData;

  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
        <Pie
          data={data}
          dataKey="value"
          nameKey="label"
          cx="50%"
          cy="50%"
          innerRadius="85%"
          outerRadius="100%"
          startAngle={90}
          endAngle={-270}
          paddingAngle={hasAnyData && data.length > 1 ? 4 : 0}
          strokeWidth={0}
          isAnimationActive={false}
          tabIndex={-1}
          style={{ outline: 'none' }}
          onClick={isClickable ? handleSliceClick : undefined}
        >
          {data.map((entry) => (
            <Cell
              key={entry.key}
              fill={entry.color}
              tabIndex={-1}
              style={{
                outline: 'none',
                cursor: isClickable ? 'pointer' : undefined,
              }}
            />
          ))}
        </Pie>
        {hasAnyData ? (
          <Tooltip
            content={<DonutTooltip />}
            cursor={false}
            wrapperStyle={{ outline: 'none', zIndex: 50 }}
          />
        ) : null}
      </PieChart>
    </ResponsiveContainer>
  );
}
